import _ from "lodash";

import { colors } from "theme/palette";

import Box from "components/Box";
import Divider from "components/Divider";
import Image from "components/Image";
import NoPrint from "components/NoPrint";
import Paper from "components/Paper";
import PrintableLayout from "components/PrintableLayout";
import Stack from "components/Stack";
import Text from "components/Text";

import BouncingDotsLoader from "pages/Deal/BouncingDotsLoader";
import PrintableMemoSectionWrapper from "pages/Deal/PrintableMemo/components/PrintableMemoSectionWrapper";

import { IDeal } from "entities/Deal/sdk";
import { IMemo, IMemoSection } from "entities/Memo/sdk";

import "pages/Deal/Memo/memo.css";

export interface IPrintableMemoContent {
  deal: IDeal;
  memo: IMemo;
  ref?: React.Ref<HTMLDivElement>;
  token?: string;
  companyLogo?: string | null;
}

const PrintableMemoContent = ({
  deal,
  memo,
  token,
  companyLogo,
  ref,
}: IPrintableMemoContent) => (
  <Paper
    ref={ref}
    sx={{
      color: colors.black,
      gap: 1.5,
      width: "100%",
      height: "100%",
      "@page": {
        marginTop: "20px",
        marginLeft: "20px",
        marginRight: "20px",
        marginBottom: "35px",
        size: "letter portrait",
      },
      "@page:first": {
        marginTop: "12px",
      },
    }}
  >
    <NoPrint>
      <BouncingDotsLoader loadingText="Document is being generated..." />
    </NoPrint>
    <PrintableLayout
      header={
        <>
          <Stack direction="row" justifyContent="space-between">
            <Stack>
              <Text variant="text4" sx={{ fontWeight: "bold" }}>
                {deal.name}
              </Text>
              <Text variant="text4">{memo.name}</Text>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                data-testid="additional-header-info"
              >
                {_.isEmpty(memo?.additional_header_info) ? (
                  <Text
                    variant="text4"
                    data-testid="additional-info-empty-value"
                  >
                    -- additional header info --
                  </Text>
                ) : (
                  <Text variant="text4" data-testid="additional-info-value">
                    {memo.additional_header_info}
                  </Text>
                )}
              </Stack>
            </Stack>
            {!_.isNil(companyLogo) && (
              <Image
                sx={{
                  height: "56px",
                  maxWidth: "50%",
                  marginBottom: 0.25,
                }}
                src={companyLogo}
              />
            )}
          </Stack>
          <Divider sx={{ marginBottom: 0.75 }} />
        </>
      }
      footer={<span></span>}
    >
      <Box
        sx={{
          "& > *": { marginBottom: 1.5, flexGrow: 1 },
        }}
      >
        {memo.sections
          .filter((section) => !section.is_na)
          .map((section: IMemoSection) => (
            <PrintableMemoSectionWrapper
              key={section.section_id}
              section={section}
              deal={deal}
              externalToken={token}
            />
          ))}
      </Box>
    </PrintableLayout>
  </Paper>
);

export default PrintableMemoContent;
